import type { LabelSettings } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch } from "@/lib/api";

const DEFAULTS: LabelSettings = {
  defaultMode: "web_direct",
  barcodeType: "code128",
  showFinancialCard: true,
  marginDots: 14,
};

export function SettingsLabelPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["admin-label-settings"],
    queryFn: () => apiFetch<LabelSettings>("/api/v1/admin/settings/label"),
  });
  const [form, setForm] = useState<LabelSettings>(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);

  async function save() {
    setSaving(true);
    try {
      await apiFetch<LabelSettings>("/api/v1/admin/settings/label", {
        method: "PUT",
        body: JSON.stringify(form),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-label-settings"] }),
        queryClient.invalidateQueries({ queryKey: ["label-settings"] }),
      ]);
      toast.success("标签设置已保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="标签设置"
        description="配置 24mm × 36mm 标签的默认打印方式、条码符号与边距；设置会立即影响新预览和打印任务。"
        actions={
          <Button onClick={save} disabled={saving || query.isLoading}>
            {saving ? "保存中…" : "保存设置"}
          </Button>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>打印与符号</CardTitle>
            <CardDescription>P900 固定 360 DPI、560 pin、24mm 胶带。</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="defaultMode">默认打印方式</Label>
              <select
                id="defaultMode"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.defaultMode}
                onChange={(event) =>
                  setForm((value) => ({
                    ...value,
                    defaultMode: event.target.value as LabelSettings["defaultMode"],
                  }))
                }
              >
                <option value="web_direct">P900 WebUSB 直连</option>
                <option value="local_driver">本地驱动打印</option>
              </select>
              <p className="text-xs text-muted-foreground">
                WebUSB 需要 Chrome/Edge 与 USB 授权；本地驱动作为兜底。
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="barcodeType">条码符号</Label>
              <select
                id="barcodeType"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.barcodeType}
                onChange={(event) =>
                  setForm((value) => ({
                    ...value,
                    barcodeType: event.target.value as LabelSettings["barcodeType"],
                  }))
                }
              >
                <option value="code128">Code128（推荐）</option>
                <option value="code39">Code39</option>
                <option value="ean13">EAN-13（需 12/13 位数字）</option>
              </select>
              <p className="text-xs text-muted-foreground">
                EAN-13 对非数字编号会自动回退 Code128，避免打印空白。
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="marginDots">左边距（dots）</Label>
                <Input
                  id="marginDots"
                  type="number"
                  min={0}
                  max={100}
                  value={form.marginDots}
                  onChange={(event) =>
                    setForm((value) => ({ ...value, marginDots: Number(event.target.value) }))
                  }
                />
                <p className="text-xs text-muted-foreground">默认 14；打印偏移时微调。</p>
              </div>
              <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.showFinancialCard}
                  onChange={(event) =>
                    setForm((value) => ({ ...value, showFinancialCard: event.target.checked }))
                  }
                />
                <span>
                  显示财务卡片编号
                  <span className="block text-xs text-muted-foreground">
                    关闭后只打印设备编号条码与二维码。
                  </span>
                </span>
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>当前取值</CardTitle>
            <CardDescription>保存后立即生效。</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-border py-2">
              <span className="text-muted-foreground">默认方式</span>
              <span>{form.defaultMode === "web_direct" ? "WebUSB" : "本地驱动"}</span>
            </div>
            <div className="flex justify-between border-b border-border py-2">
              <span className="text-muted-foreground">条码</span>
              <span>{form.barcodeType}</span>
            </div>
            <div className="flex justify-between border-b border-border py-2">
              <span className="text-muted-foreground">财务卡片</span>
              <span>{form.showFinancialCard ? "显示" : "隐藏"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">左边距</span>
              <span>{form.marginDots} dots</span>
            </div>
            <p className="pt-2 text-xs text-muted-foreground">
              标签尺寸固定 24mm × 36mm。修改后到“标签任务”重新预览或打印测试标签。
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
